import { Socket, io } from 'socket.io-client';

import { CenterPoint } from 'hooks/use-map/types';
import { getConfigValue } from 'utils/environment';

export enum EVENT_TYPE {
  CUSTOMER_CONNECT = 'customer connect',
  ORDER_UPDATE_EVENT = 'order update',
  WATCH_DRIVER = 'watch driver',
  WATCH_ORDER = 'watch order',
  LOCATION_UPDATE = 'location update',
}

interface Credentials {
  order_uuid: string;
  access_token: string;
  share_uuid?: string;
}

type BringgOrder = {
  driver?: CenterPoint;
  expired?: boolean;
  driver_uuid?: string;
};

type SubscriptionCallbackValue =
  | (BringgOrder & { lat: never; lng: never })
  | (CenterPoint & { driver: never; driver_uuid: never });

type SubscriptionCallback = (value: SubscriptionCallbackValue) => void;

export class Bringg {
  _endpoint = getConfigValue({
    key: 'bringg_realtime', // Does not existe yet
    defaultValue: 'https://us2-realtime.bringg.com',
  });
  _socket: Socket;
  _watchingDriver = false;
  _watchingOrder = false;

  constructor(apiKey: string) {
    this._socket = io(this.getRealTimeEndpoint(), {
      transports: ['websocket', 'polling'],
      secure: true,
      port: 443,
    });

    this._socket.emit(EVENT_TYPE.CUSTOMER_CONNECT, { customer_access_token: apiKey });

    this._socket.on('connect', () => {
      // eslint-disable-next-line no-console
      console.log(`Bringg websocket connected`);
    });

    this._socket.on('disconnect', (id: string) => {
      // eslint-disable-next-line no-console
      console.log(`Bringg ${id} websocket connected`);
      this._watchingDriver = false;
      this._watchingOrder = false;
    });

    this._socket.on('error', e => {
      // eslint-disable-next-line no-console
      console.warn('Websocket error: ', e);
    });
  }

  private getRealTimeEndpoint() {
    return this._endpoint;
  }

  public disconnect() {
    this._socket.disconnect();
  }

  private reconnectExpired(credentials: Credentials, callback: SubscriptionCallback) {
    const { order_uuid, access_token } = credentials;
    this._watchingDriver = false;
    this._watchingOrder = false;

    this.watchOrder({ order_uuid, access_token }, callback);
  }

  private subscribe(event: EVENT_TYPE, credentials: Credentials, callback?: SubscriptionCallback) {
    this._socket.emit(event, credentials, callback);
  }

  private unsubscribe(event: EVENT_TYPE) {
    this._socket.off(event);
  }

  public watchOrder(credentials: Credentials, callback: SubscriptionCallback) {
    const returnValue = {
      unsubscribe: () => {
        this.unsubscribe(EVENT_TYPE.WATCH_ORDER);
        if (this._watchingDriver) {
          this.unsubscribe(EVENT_TYPE.WATCH_DRIVER);
        }
      },
    };

    if (this._watchingOrder) {
      return returnValue;
    }

    this.subscribe(EVENT_TYPE.WATCH_ORDER, credentials, order => {
      this._watchingOrder = true;

      if ('expired' in order && order.expired === true) {
        this.reconnectExpired(credentials, callback);
      }

      callback(order);
      return;
    });

    this._socket.on(EVENT_TYPE.ORDER_UPDATE_EVENT, order => {
      if (!this._watchingDriver && order.driver_uuid) {
        this._watchingDriver = true;
        // Automatically subscribe to Watch driver. This will trigger messages to observers of location update event
        this._socket.emit(EVENT_TYPE.WATCH_DRIVER, {
          ...credentials,
          driver_uuid: order.driver_uuid,
        });
      }
      callback(order);
    });

    return returnValue;
  }

  public locationUpdate(credentials: Credentials, callback: SubscriptionCallback) {
    this.unsubscribe(EVENT_TYPE.LOCATION_UPDATE);

    this._socket.on(EVENT_TYPE.LOCATION_UPDATE, callback);
    this._socket.emit(EVENT_TYPE.LOCATION_UPDATE, credentials);

    return { unsubscribe: this.unsubscribe.bind(this, EVENT_TYPE.LOCATION_UPDATE) };
  }
}
